import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  AiAgentsNamespaceDeleteDocumentSourceItemArgs,
  Mutation,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { useConfirmationModalSize } from 'src/components/pages/ai-agents/components/confirmation-modal-parts';
import { DELETE_DOCUMENT_SOURCE_ITEM_MUTATION } from 'src/components/pages/ai-agents/knowledge-base-sources/documents/queries';
import { useAgentSourceDeleteDialog } from 'src/components/pages/ai-agents/knowledge-base-sources/hooks/use-agent-source-delete-dialog';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

export const useDeleteDocumentSourceItem = ({
  onDeleted,
  aiAgentId,
}: {
  onDeleted: VoidFunction;
  aiAgentId?: string;
}) => {
  const { t } = useTranslation(['aiAgents']);
  const { id: companyId } = useCurrentInstitution();
  const showConfirmationModal = useShowConfirmationModal();
  const showNotification = useShowNotification();
  const handleGraphQLError = useHandleGraphQLError();
  const modalSize = useConfirmationModalSize();
  const agentSourceDeleteDialog = useAgentSourceDeleteDialog();

  const [deleteDocument] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceDeleteDocumentSourceItemArgs
  >(DELETE_DOCUMENT_SOURCE_ITEM_MUTATION, { onError: handleGraphQLError });

  return useCallback(
    (document: { id: string }) => {
      const onConfirm = async () => {
        const result = await deleteDocument({
          variables: { input: { companyId, id: document.id } },
        });

        if (result.data?.aiAgents.deleteDocumentSourceItem.success) {
          showNotification({
            type: 'success',
            title: t('aiAgents:knowledge.documents.delete.success'),
          });
          onDeleted();
        }
      };

      const modalProps = {
        testId: 'document-delete-modal',
        ...modalSize,
      };

      if (aiAgentId) {
        void agentSourceDeleteDialog({
          aiAgentId,
          sourceId: document.id,
          onConfirm: () => void onConfirm(),
        }).then(
          (dialog) => dialog && showConfirmationModal({ ...modalProps, ...dialog }),
        );

        return;
      }

      showConfirmationModal({
        ...modalProps,
        title: t('aiAgents:knowledge.documents.delete.title'),
        message: t('aiAgents:knowledge.documents.delete.message'),
        confirmButtonText: t('aiAgents:knowledge.documents.delete.confirm'),
        confirmButtonType: 'delete',
        onConfirm,
      });
    },
    [
      aiAgentId,
      agentSourceDeleteDialog,
      companyId,
      deleteDocument,
      modalSize,
      onDeleted,
      showConfirmationModal,
      showNotification,
      t,
    ],
  );
};
